import { HttpClient } from '@angular/common/http';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
    Subject,
    Subscription,
    takeWhile,
    timer,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DateTime } from 'luxon';
import { UserInfo, UserService } from './user.service';
import { ConfirmationDialogService } from '@polk_county_public_schools/ngx-pcps-dialogs/confirmation';
import { MessageBoxDialogService } from '@polk_county_public_schools/ngx-pcps-dialogs/messagebox';
import { CountdownDialogService } from '@polk_county_public_schools/ngx-pcps-dialogs/countdown';
import { AppService } from './app.service';
import { SnackBarService } from '@polk_county_public_schools/ngx-pcps-dialogs/snackbar';
import { ErrorService } from './error.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
    protected readonly destroyRef = inject(DestroyRef);
    protected readonly http = inject(HttpClient);
    protected readonly userSvc = inject(UserService);
    protected readonly router = inject(Router);
    protected readonly confirmDlgSvc = inject(ConfirmationDialogService);
    protected readonly messageDlgSvc = inject(MessageBoxDialogService);
    protected readonly countdownDlgSvc = inject(CountdownDialogService);
    protected readonly snackBarSvc = inject(SnackBarService);
    protected readonly errorSvc = inject(ErrorService);
    protected readonly appSvc = inject(AppService);
    static IDLEPROMPTTIME: number = 25 * 60 * 1000; // 25 minutes (time before logout dialog appears)
    static AUTHREFRESHTIME: number = 29 * 60 * 1000; // 29 minutes (time period to wait between authtoken refreshes)
    static DIALOGTIME: number = 2 * 60; // 2 minutes (how long to show logout dialog before automatically logging out)
    HasVersionUpdate: Subject<boolean> = new Subject<boolean>();
    BuildTime: Subject<string> = new Subject<string>();
    VersionNumber: Subject<string> = new Subject<string>();
    idleTimerSub: Subscription | null = null;
    authTimerSub: Subscription | null = null;

    constructor(
    ) {
        // Set up the subscription to watch for the user being updated, so we can tell if we need to boot up auth token refreshing.
        this.userSvc.UserSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user) => {
            //AuthToken refreshing and Idle timer summary:
            //start idle prompt timer and authRefresh interval
            //Refresh AuthToken every x minutes until logged out
            //Idle timer waits y minutes and then displays logout dialog
            //Everytime navigate to new page, refresh idle timer
            //If logout dialog appears and user selects stay logged in, refresh idle timer
            //if user selects Logout or lets dialog timer expire, they get logged out
            //On logout, cancel idle timer and auth refresh interval

            if (user && user.AuthToken) {
                this.appSvc.checkAuthToken(user.AuthToken).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((auth) => {
                    //AuthToken expires 1 hour from last AuthTokenRefreshed
                    let expirationTime = DateTime.fromISO(
                        auth.AuthTokenRefreshed
                    ).plus({ hours: 1 });
                    //how long from now to when expires
                    let timeTillExpire = expirationTime.diff(
                        DateTime.now()
                    );

                    if (this.authTimerSub == null) {
                        //if time till expiration is less than time till refresh, refresh authtoken. subtract a minute for a more generous leeway
                        if (
                            timeTillExpire.minus({ minutes: 1 }).toMillis() <
                            AuthService.AUTHREFRESHTIME
                        ) {
                            this.refreshAuthToken();
                        }

                        this.authTimerSub = timer(AuthService.AUTHREFRESHTIME).pipe(takeWhile((w) => this.userSvc.User != null)).subscribe((val) => {
                            this.refreshAuthToken();
                        });
                    }
                    else {
                        this.resetAuthTimer();
                    }

                    if (this.idleTimerSub == null) {
                        this.idleTimerSub = timer(AuthService.IDLEPROMPTTIME).pipe(takeWhile((w) => this.userSvc.User != null)).subscribe((val) => {
                            this.idlePrompt();
                        });
                    }
                    else {
                        this.resetIdleTimer();
                    }
                });
            }
        });
    }

    resetIdleTimer() {
        if (this.idleTimerSub != null) {
            this.idleTimerSub.unsubscribe();

            this.idleTimerSub = timer(AuthService.IDLEPROMPTTIME)
                .pipe(takeWhile((w) => this.userSvc.User != null))
                .subscribe((val) => {
                    this.idlePrompt();
                });
        }
    }

    resetAuthTimer() {
        if (this.authTimerSub != null) {
            this.authTimerSub.unsubscribe();

            this.authTimerSub = timer(AuthService.AUTHREFRESHTIME)
                .pipe(takeWhile((w) => this.userSvc.User != null))
                .subscribe((val) => {
                    this.refreshAuthToken();
                });
        }
    }

    idlePrompt() {
        //pop up dialog to ask if user wants to stay logged in
        let dialog = this.countdownDlgSvc.Open({
            disableClose: true,
            TitleText: 'Session Inactivity Logout',
            MessageText:
                'You are about to be automatically logged out of the system due to inactivity. If you wish to stay logged in to the site, please click the "Stay Logged In" button below.',
            OKText: 'Stay Logged In',
            CancelText: 'Log Out',
            CountdownTime: AuthService.DIALOGTIME,
        });

        dialog.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res) => {
            if (res === true) {
                // User wants to stay logged in
                this.resetIdleTimer();
            } else {
                // User wishes to logout, or the timer expired. Either way, log them out.
                this.userSvc.logout();
            }
        });
    }

    ProcessLogin(user: UserInfo) {
        // Route to the proper home component for the user. Prefer in this order:
        // developer, admin, student
        let role: string;

        if (user != null) {
            if (user.RoleBooleans.IsDeveloper) {
                role = 'Developer';
            } else if (user.RoleBooleans.IsAdmin) {
                role = 'Admin';
            } else if (user.RoleBooleans.IsStudent) {
                role = 'Student';
            } else {
                // No matching role - no access
                this.messageDlgSvc.Open({
                    TitleText: 'Alert - No Access Rights Detected',
                    MessageText:
                        'You do not currently have access rights to the PEF Scholarship System',
                });

                // Logout the user
                this.userSvc.logout(false, 2);
            }

            // Update the current role field
            user.CurrentRole = role;

            // Now, persist it to session storage and then broadcast it for listeners
            this.userSvc.SetUser(user);

            // Go to the home page for this role
            if (role === 'Student') {
                this.router.navigate(['/student-application']);
                return;
            }
            this.router.navigate(['/admin-home']);
        } else {
            //user object is null
            this.messageDlgSvc.Open({
                TitleText: 'Alert - User Object NULL',
                MessageText: 'There was an issue loading your user information',
            });

            // Logout the user
            this.userSvc.logout(false, 2);
        }
    }

    refreshAuthToken() {
        let authToken = this.userSvc.User.AuthToken;

        this.appSvc.refreshAuthTokenCall(authToken)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (newAuthPacket) => {
                    let newAuth = newAuthPacket.AuthToken || newAuthPacket;

                    // We have a valid, new auth token. Update the User variable in the userService to reflect this.
                    this.userSvc.UpdateAuthToken(newAuth);
                },
                error: (error) => {
                    this.errorSvc.HandleServiceError(error);
                }
            });
    }



}

export class AuthPacket {
    AuthToken: string;
}

export interface PasswordResetRequestPacket {
    Username: string;
    StudentID: string;
    DateOfBirth: Date;
}

export interface PasswordResetTokenStatus {
    Valid: boolean;
    ErrorMessage: string | null;
}

export class UserAuth {
    UserAuthId: number;
    UserId: number;
    SystemMasterId: number;
    AuthToken: string;
    AuthTokenRefreshed: string;
    AuthTokenExpired: boolean;
    RecentUse: boolean;
    ImpersonatorSapid: string;
    ImpersonatorPositionNo: string;
    TrainingMode: boolean;
    ValToken: string;
    LastActionTime: Date;
    FocusStaffId: number;
}
